import { useParams, Link } from 'react-router-dom';
import { videos } from '@/mocks/videos';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SeoHead from '@/components/feature/SeoHead';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

const articleContentMap: Record<string, string[]> = {
  v001: [
    '北欧极简风格近年来在全球范围内备受追捧，它不仅仅是一种家居风格，更代表了一种追求简约、自然与舒适的生活态度。在本期视频中，我们将带你深入探索北欧极简家居的精髓所在。',
    '色彩是北欧风格的灵魂。北欧极简家居以白色、米色、灰色等中性色调为主，营造明亮通透的空间感。适当加入原木色和绿植元素，可以让空间充满自然气息与生机。关键在于保持色调的统一性，避免过于花哨的色彩组合。',
    '家具选择上，北欧风格强调功能性与设计感的平衡。选择线条简洁的家具，避免繁复的装饰。一张简约的浅色布艺沙发、一个原木质感的茶几、几把设计经典的椅子，就足以营造出舒适又有格调的空间。材质上以天然材料为主，如实木、棉麻、羊毛等。',
    '收纳是北欧极简风保持整洁的关键。利用墙面空间安装开放式搁板，既能展示心爱的书籍和摆件，又不会让空间显得拥挤。选择带有储物功能的家具，如储物床、收纳凳等，可以巧妙地隐藏日常杂物。',
    '灯光设计同样不可忽视。北欧冬季漫长，光线显得尤为珍贵。多层次照明设计——主灯、落地灯、壁灯、台灯的组合，可以营造温暖舒适的氛围。选择温暖色温的灯泡（2700K-3000K），让家更有温馨感。',
    '最后，软装是画龙点睛之笔。几幅简约的艺术画作、几个柔软的抱枕、一条温暖的毛毯，都能为空间增添层次感和舒适度。记住北欧风格的核心：少即是多，让每一件物品都发挥它该有的价值。',
  ],
};

function getArticleContent(id: string): string[] {
  return articleContentMap[id] || [
    '本期视频为大家带来了精彩的内容，我们将深入探讨这个有趣的话题。无论你是新手还是资深爱好者，都能从中获得新的启发和收获。',
    '在这个话题中，有很多值得关注的细节和技巧。我们的团队经过了精心的准备和研究，确保每一个观点都有据可循，每一个步骤都清晰易懂。',
    '通过实际案例的分享，我们能更好地理解这些内容在实际生活中的应用。这些案例来自真实用户和专业人士的经验总结，具有很高的参考价值。',
    '我们还会分享一些实用的建议和技巧，帮助大家更好地理解和运用这些知识。记住，学习和探索是一个持续的过程，保持好奇心才能不断进步。',
    '最后，感谢大家的观看和支持！如果你喜欢这期内容，不要忘记点赞、收藏，还可以分享给身边的朋友。关注蜜桃视频，获取更多精彩内容！',
  ];
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <div className="w-full px-4 md:px-6 pt-24 pb-12 min-h-screen bg-background-50">
        <div className="max-w-4xl mx-auto text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">视频未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">您要查看的视频不存在或已被移除</p>
          <Link
            to="/"
            className="px-5 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleContent = getArticleContent(video.id);
  const relatedVideos = videos
    .filter((v) => v.category === video.category && v.id !== video.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: video.categoryName, path: `/category/${video.category}` },
    { name: video.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    author: {
      '@type': 'Person',
      name: video.author,
    },
    datePublished: video.date,
    publisher: {
      '@type': 'Organization',
      name: '蜜桃视频',
    },
    image: video.imageUrl,
  };

  return (
    <>
      <SeoHead
        title={`${video.title} - 蜜桃视频`}
        description={video.description}
        keywords={`蜜桃视频,${video.tags.join(',')},${video.categoryName}视频`}
        canonicalUrl={`https://mitaovideo.com/article/${video.id}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <article className="w-full px-4 md:px-6 pt-24 pb-12 md:pb-16 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-6">
            <span className="inline-block text-xs bg-primary-50 text-primary-600 px-2.5 py-1 rounded-full mb-3">
              {video.categoryName}
            </span>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {video.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {video.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {video.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {formatViews(video.views)} 次观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i> {formatViews(video.likes)} 点赞
              </span>
            </div>
          </div>

          <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-8">
            <img
              src={video.imageUrl}
              alt={video.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-500 transition-colors">
                <i className="ri-play-fill text-2xl md:text-3xl ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2.5 py-1 rounded">
              {video.duration}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {video.tags.map((tag) => (
              <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full">
                #{tag}
              </span>
            ))}
          </div>

          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 p-4 bg-background-100 rounded-lg">
            <strong className="text-foreground-900">视频简介：</strong>{video.description}
          </p>

          <div className="prose-custom space-y-5">
            {articleContent.map((paragraph, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          {relatedVideos.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedVideos.map((rv) => (
                  <Link
                    key={rv.id}
                    to={`/article/${rv.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={rv.imageUrl}
                        alt={rv.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                        {rv.duration}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {rv.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1.5">
                        {formatViews(rv.views)} 次观看
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}